import React from 'react';
import styled from 'styled-components';
import { AlertIcon, BanIcon, CheckIcon, InfoIcon, XIcon } from '../../ui/icons';

/**
 * Baner komunikatu: JEDEN zamiast pieciu.
 *
 * Do tej pory ten sam komunikat "cos poszlo nie tak" mial w aplikacji piec roznych postaci,
 * kazda skladana osobno na stronie, ktora akurat jej potrzebowala:
 *
 *   - `ErrorMessage` (klienci)        — tlo #fef2f2, tekst #dc2626, ramka #fecaca, padding 16/20, promien 12;
 *   - `ErrorBanner` (pakiety, cenniki) — rgba(239,68,68,.08), padding 12/16, promien 10;
 *   - `ErrorBanner` (magazyn)          — rgba(239,68,68,.12) z tekstem #7f1d1d, czyli w trybie ciemnym
 *                                        ciemnoczerwony tekst na prawie czarnym tle: nieczytelny;
 *   - `FieldError` (rezerwacja)        — sam czerwony tekst pod polem;
 *   - wariant z ImportData.js          — jako JEDYNY zbudowany na tokenach i dlatego jedyny poprawny
 *                                        w obu motywach. To on jest tu wzorcem.
 *
 * Stany "sukces" i "ostrzezenie" powstawaly ad hoc, wiec problem dotyczyl nie tylko bledow.
 *
 * DLACZEGO WYLACZNIE TOKENY ZNACZENIOWE (`--danger`, `--danger-bg`, `--danger-border` i analogicznie
 * dla `--success-*`, `--warning-*`, `--info-*`): kazdy z nich ma OSOBNA wartosc dla trybu ciemnego
 * (index.css). Kolor wpisany wprost tej wiedzy nie ma i dlatego wlasnie powstal wariant z magazynu.
 * Zadnej wartosci koloru nie ma w tym pliku ani jednej — to jest sedno komponentu, nie ozdobnik.
 *
 * DLACZEGO NIE EMOTKA jako ikona: emotka nie dziedziczy motywu (w ciemnym zostaje jaskrawa plama),
 * wyglada inaczej na kazdym systemie, a czytnik ekranu odczytuje jej pelna nazwe posrodku zdania.
 * Ikony pochodza z `ui/icons.js` i rysuja sie `currentColor`, wiec same przejmuja kolor tonu.
 */

/* Kazdy ton ma WLASNY znak, bo ta sama ikona w kilku stanach nie niesie juz zadnej informacji:
   `danger` to BanIcon (odmowa/blokada), `warning` AlertIcon (trojkat z wykrzyknikiem), `success`
   CheckIcon, `info` InfoIcon. Tej ostatniej pierwotnie w zestawie nie bylo — zostala dolozona do
   `ui/icons.js` razem z ta fala prac, zamiast naciagac znaczenie ikony ostrzezenia. Wywolujacy
   moze podac wlasna przez `icon`. */
const TONES = {
  info: { fg: 'var(--info)', bg: 'var(--info-bg)', border: 'var(--info-border)', Icon: InfoIcon },
  success: { fg: 'var(--success)', bg: 'var(--success-bg)', border: 'var(--success-border)', Icon: CheckIcon },
  warning: { fg: 'var(--warning)', bg: 'var(--warning-bg)', border: 'var(--warning-border)', Icon: AlertIcon },
  danger: { fg: 'var(--danger)', bg: 'var(--danger-bg)', border: 'var(--danger-border)', Icon: BanIcon },
};

const Wrap = styled.div`
  display: flex;
  /* Przy wielu liniach tresci ikona ma stac przy PIERWSZEJ linii, a nie posrodku bloku.
     W wersji jednoliniowej (compact) srodek jest wlasnie tym, czego oko oczekuje. */
  align-items: ${(p) => (p.$compact ? 'center' : 'flex-start')};
  gap: ${(p) => (p.$compact ? 'var(--sp-2)' : 'var(--sp-3)')};
  padding: ${(p) => (p.$compact ? 'var(--sp-2) var(--sp-3)' : 'var(--sp-3) var(--sp-4)')};
  border-radius: var(--r-md);
  border: 1px solid ${(p) => p.$tone.border};
  background: ${(p) => p.$tone.bg};
  color: ${(p) => p.$tone.fg};
  font-size: ${(p) => (p.$compact ? 'var(--fs-sm)' : 'var(--fs-base)')};
  line-height: 1.45;
`;

const IconSlot = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  /* Ikona ma wysokosc linii pisma, wiec w wersji nie-compact opada o wlos ponizej gornej
     krawedzi tekstu i wyglada na przyklejona do gory. */
  padding-top: ${(p) => (p.$compact ? '0' : '1px')};
`;

const Body = styled.div`
  /* min-width: 0 bez tego dlugi komunikat bez spacji (np. adres URL) rozpycha caly baner
     poza szerokosc karty, zamiast sie zawinac. */
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-weight: 700;
  font-size: var(--fs-base);
  margin-bottom: var(--sp-1);
`;

/* Przyciski w banerze celowo NIE korzystaja z PrimaryButton/SecondaryButton: tamte niosa wlasne
   tlo i cien, ktore w srodku kolorowego banera walcza z jego tonem. Tutaj oba dziedzicza kolor
   tonu przez `currentColor`, wiec dzialaja w kazdym z czterech wariantow i w obu motywach. */
const ActionButton = styled.button`
  flex-shrink: 0;
  background: transparent;
  color: inherit;
  border: 1px solid currentColor;
  border-radius: var(--r-sm);
  padding: var(--sp-1) var(--sp-3);
  font-size: var(--fs-sm);
  font-weight: 600;
  cursor: pointer;
  transition: opacity var(--dur) var(--ease);

  &:hover:not(:disabled) {
    /* Przyciemnienie przez przezroczystosc, a nie przez drugi kolor — inaczej wrocilaby tu
       wartosc wpisana wprost, ktora nie zna trybu ciemnego. */
    opacity: 0.75;
  }
`;

const DismissButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: inherit;
  border: none;
  border-radius: var(--r-sm);
  padding: var(--sp-1);
  cursor: pointer;
  transition: opacity var(--dur) var(--ease);

  &:hover {
    opacity: 0.7;
  }
`;

export function Banner({
  tone = 'info',
  title,
  children,
  icon,
  onDismiss,
  action,
  compact,
  style,
  'data-cy': dataCy,
}) {
  const toneStyle = TONES[tone] || TONES.info;
  const iconSize = compact ? 16 : 20;
  const DefaultIcon = toneStyle.Icon;
  const iconNode = icon !== undefined ? icon : DefaultIcon ? <DefaultIcon size={iconSize} /> : null;

  return (
    <Wrap
      $tone={toneStyle}
      $compact={!!compact}
      style={style}
      /* `alert` przerywa czytnikowi ekranu biezaca wypowiedz, bo blad zwykle blokuje dalsza prace.
         Pozostale tony sa tylko informacja i `status` ogloszy je, gdy czytnik skonczy zdanie. */
      role={tone === 'danger' ? 'alert' : 'status'}
      data-cy={dataCy}
    >
      {iconNode ? <IconSlot $compact={!!compact}>{iconNode}</IconSlot> : null}

      <Body>
        {/* `compact` to wersja pod pole formularza — tam tytul jest zawsze powtorzeniem etykiety pola. */}
        {title && !compact ? <Title>{title}</Title> : null}
        {children}
      </Body>

      {action && action.label ? (
        <ActionButton type="button" onClick={action.onClick} data-cy={action.dataCy}>
          {action.label}
        </ActionButton>
      ) : null}

      {onDismiss ? (
        <DismissButton type="button" onClick={onDismiss} aria-label="Zamknij">
          <XIcon size={compact ? 14 : 16} />
        </DismissButton>
      ) : null}
    </Wrap>
  );
}

export default Banner;
